import { formatBandwidth, formatSimplifiedTimeForDisplay, normalizeTimestamp } from './helpers'
import { GraphDataPoint } from './types'

export const formatYAxisBandwidth = (value: number) => {
  return formatBandwidth(value, true, false);
}

export const formatYAxisLatency = (value: number) => {
  return `${(value * 1000).toFixed(0)} ms`;
}

export const formatYAxisLoss = (value: number) => {
  return `${value.toFixed(1)}%`;
}

export const formatYAxisDroppedPackets = (value: number) => {
  return `${value.toFixed(1)}%`;
}

export const getFixedTimeIntervals = (data: GraphDataPoint[], isExpanded: boolean): GraphDataPoint[] => {
  if (data.length === 0) return [];
  if (isExpanded) return data;

  const timestamps = data.map(point => normalizeTimestamp(point.timestamp).getTime());
  const maxTimestamp = Math.max(...timestamps);
  const latestDate = new Date(maxTimestamp);
  
  const roundedLatestDate = new Date(latestDate);
  const hours = roundedLatestDate.getHours();
  const roundedHours = Math.floor(hours / 6) * 6;
  roundedLatestDate.setHours(roundedHours, 0, 0, 0);
  
  const intervalTimes: Date[] = [];
  
  for (let i = 0; i < 4; i++) {
    const intervalTime = new Date(roundedLatestDate);
    intervalTime.setHours(roundedLatestDate.getHours() - (i * 6));
    intervalTimes.unshift(intervalTime);
  }

  const result: GraphDataPoint[] = [];

  for (const intervalTime of intervalTimes) {
    let closestPoint: GraphDataPoint | null = null;
    let minTimeDiff = Infinity;

    for (const point of data) {
      const pointTime = normalizeTimestamp(point.timestamp);
      const timeDiff = Math.abs(pointTime.getTime() - intervalTime.getTime());

      if (timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        closestPoint = { ...point };
      }
    }

    if (closestPoint) {
      result.push({
        timestamp: intervalTime.toISOString(),
        time: intervalTime.toISOString(),
        formattedTime: intervalTime.toISOString(),
        download: closestPoint.download,
        upload: closestPoint.upload,
        ping: closestPoint.ping,
        loss: closestPoint.loss,
        droppedPacketsDownload: closestPoint.droppedPacketsDownload,
        droppedPacketsUpload: closestPoint.droppedPacketsUpload
      });
    }
  }

  return result;
};

export interface DisplayDataPoint extends GraphDataPoint {
  simplifiedTime: string;
}

export const addSimplifiedTimeToData = (data: GraphDataPoint[]): DisplayDataPoint[] => {
  return data.map(point => ({
    ...point,
    simplifiedTime: formatSimplifiedTimeForDisplay(point.timestamp)
  }));
};

export const getXAxisTicks = (
  legendData: GraphDataPoint[] | undefined,
  isExpanded: boolean
): string[] | undefined => {
  if (isExpanded || !legendData) return undefined;
  
  const legendIntervals = getFixedTimeIntervals(legendData, false);
  return legendIntervals
    .map(point => formatSimplifiedTimeForDisplay(point.timestamp))
    .filter((tick): tick is string => !!tick);
};

export const filterInitialZeroValues = (
  data: DisplayDataPoint[],
  valueKeys: string[] = ['download', 'upload']
): DisplayDataPoint[] => {
  if (data.length === 0) return [];
  
  let firstNonZeroIndex = 0;
  for (let i = 0; i < data.length; i++) {
    const hasNonZeroValue = valueKeys.some(key => 
      data[i][key as keyof DisplayDataPoint] as number > 0
    );
    
    if (hasNonZeroValue) {
      firstNonZeroIndex = i;
      break;
    }
  }
  
  return data.slice(firstNonZeroIndex);
};

export const prepareGraphData = (
  data: GraphDataPoint[],
  legendData: GraphDataPoint[] | undefined,
  isExpanded: boolean,
  valueKeys: string[] = ['download', 'upload']
): {
  displayData: DisplayDataPoint[];
  filteredDisplayData: DisplayDataPoint[];
  xAxisTicks: string[] | undefined;
} => {
  const displayData = addSimplifiedTimeToData(data);
  const xAxisTicks = getXAxisTicks(legendData, isExpanded);
  
  const shouldFilter = !valueKeys.some(key => key.includes('loss') || key.includes('droppedPackets'));
  const filteredDisplayData = shouldFilter
    ? filterInitialZeroValues(displayData, valueKeys)
    : displayData;
    
  return {
    displayData,
    filteredDisplayData,
    xAxisTicks
  };
};
